import { Stack, Typography } from '@mui/material'
import { useCallback } from 'react'
import { Button, TextInputField } from 'utoniq-ui'

import {
  MembershipInterval,
  RegisterSubscriptionsInput,
} from '@/clients/utoniq-core/schema'
import {
  handleError,
  tLabel,
  useForm,
  UseFormProps,
  useMessage,
  useTranslation,
} from '@/core'
import { Organism, OrganismFrame } from '@/core/architecture'

import { PriceInputCell } from './components'

export type MembershipCreateFormProps = {
  onSubmit: (values: CreateMembershipProps) => Promise<void>
}

export type CreateMembershipProps = RegisterSubscriptionsInput

const schema: UseFormProps['schema'] = o => ({
  title: o
    .string()
    .max(50)
    .allow(null)
    .optional()
    .label(tLabel('consoleMembershipCreate.title')),
  content: o
    .string()
    .max(2000)
    .label(tLabel('consoleMembershipCreate.content')),
  annualContent: o
    .string()
    .allow(null, '')
    .optional()
    .max(2000)
    .label(tLabel('consoleMembershipCreate.annualContent')),
  plans: o.array().items({
    amount: o.number(),
    interval: o.string(),
  }),
})

export const MembershipCreateForm: Organism<MembershipCreateFormProps> = ({
  onSubmit,
}) => {
  const { t } = useTranslation('consoleMembershipCreate')
  const { showError } = useMessage()

  const {
    handleSubmit,
    register,
    setValue,
    setError,
    watch,
    control,
    formState: { isSubmitting, isValid, errors },
  } = useForm<CreateMembershipProps>({
    schema,
    defaultValues,
  })

  const planWatch = watch('plans')
  const hasYearlyPlan = planWatch?.length === 2

  const handleOnSubmit = useCallback(
    async (values: CreateMembershipProps) => {
      await onSubmit(values).catch(error => {
        handleError<CreateMembershipProps>(error, setError, showError)
      })
    },
    [onSubmit, setError, showError]
  )

  return (
    <OrganismFrame sx={{ px: 5 }}>
      <TextInputField
        type="input"
        t={t}
        textFieldProps={{ sx: { mb: 0 } }}
        titleProps={{ variant: 'h5', color: 'text.primary' }}
        {...register('title')}
        error={errors?.title}
      />
      <PriceInputCell
        watch={watch}
        setValue={setValue}
        hasYearlyPlan={hasYearlyPlan}
        control={control}
        register={register}
      />
      <TextInputField
        type="textarea"
        t={t}
        textFieldProps={{
          multiline: true,
          rows: 13,
          sx: { mb: 0 },
          InputProps: { sx: { px: 4, py: 3 } },
        }}
        titleProps={{ variant: 'h5', color: 'text.primary' }}
        {...register('content')}
        error={errors?.content}
      />
      {hasYearlyPlan && (
        <TextInputField
          type="textarea"
          t={t}
          textFieldProps={{
            multiline: true,
            rows: 13,
            sx: { mb: 0 },
            InputProps: { sx: { px: 4, py: 3 } },
          }}
          titleProps={{ variant: 'h5', color: 'text.primary' }}
          {...register('annualContent')}
          error={errors?.annualContent}
        />
      )}
      <Typography variant="subtitle1" sx={{ mt: 7 }}>
        {t('aboutSales')}
      </Typography>
      <Stack direction="row" sx={{ mt: 2 }}>
        <Typography variant="overline">・</Typography>
        <Typography variant="caption">{t('aboutSalesDetail1')}</Typography>
      </Stack>
      <Stack direction="row" sx={{ mt: 2 }}>
        <Typography variant="overline">・</Typography>
        <Typography variant="caption">{t('aboutSalesDetail2')}</Typography>
      </Stack>
      <Stack sx={{ mt: 6, mb: 4 }} justifyContent="center" alignItems="center">
        <Button
          sx={{ width: 244, py: 3 }}
          onClick={handleSubmit(handleOnSubmit)}
          loading={isSubmitting}
          disabled={!isValid || isSubmitting}
        >
          {t('startMembership')}
        </Button>
      </Stack>
    </OrganismFrame>
  )
}

const defaultValues: CreateMembershipProps = {
  title: '',
  content: '',
  annualContent: null,
  plans: [{ amount: 480, interval: MembershipInterval.Month }],
}

export const monthlyFees = [
  300, 480, 500, 780, 800, 980, 1000, 1180, 1200, 5000, 8000, 10000,
].map(amount => ({ value: amount.toString(), label: amount.toString() }))
